import { reactive, computed, watch } from 'vue'
import { isActive } from '@/services/feature-flag-service'
import {
  info,
  isLoggingEnabled,
  isGroupEnabled,
  enableLogging,
  enableGroup,
} from '@/lib/logger'
import type { MapManager } from '@onxmaps/map-x'
import { WEBMAP_MAP_MANAGER_KEY } from '@/services/map-provider/injection-key'

/**
 * Instance storage for MapManager(s)
 * Supports multiple map instances via symbol keys
 */
const instances = new Map<symbol, MapManager>()

/**
 * Set the MapManager instance for a given key
 * @param manager - The MapManager instance to store
 * @param key - Optional symbol key for multi-map support (defaults to primary key)
 */
export const setMapManager = (
  manager: MapManager,
  key: symbol = WEBMAP_MAP_MANAGER_KEY
) => {
  instances.set(key, manager)
}

/**
 * Get the MapManager instance for a given key
 * Use this when inject() is not available (e.g., in watchers, lifecycle hooks, utility functions)
 * @param key - Optional symbol key for multi-map support (defaults to primary key)
 * @returns MapManager instance or null if not yet initialized
 */
export const getMapManager = (
  key: symbol = WEBMAP_MAP_MANAGER_KEY
): MapManager | null => {
  return instances.get(key) ?? null
}

// Provider state - tracks FF status and readiness
const activeViewer = computed(() => {
  const viewer = isActive('map_x_viewer') ? 'Onyx' : 'Mapbox'
  return viewer
})

const isMapXViewerDebugActive = computed(() => isActive('map_x_viewer_debug'))

/**
 * Per-key state storage for MapManager instances
 * Supports multiple map instances with independent state tracking
 */
interface MapInstanceState {
  isMapManagerReady: boolean
  isMapViewerReady: boolean
}

const instanceStates = reactive(new Map<symbol, MapInstanceState>())

/**
 * Reactive state for Map Manager Provider (global viewer configuration)
 */
export const mapManagerProviderState = reactive({
  /**
   * The active map viewer, based on feature flag 'map_x_viewer'
   */
  activeViewer: activeViewer,
})

/**
 * Setters & getters for Map Manager Provider State
 */

/**
 * *(AI-generated)*
 * Returns the currently active map viewer ('Onyx' or 'Mapbox'), as determined by the 'map_x_viewer' feature flag.
 */
export const getActiveViewer = () => mapManagerProviderState.activeViewer

/**
 * Set MapManager ready state for a given key
 * @param ready - Boolean flag indicating MapManager is instantiated
 * @param key - Optional symbol key for multi-map support (defaults to primary key)
 */
export const setMapManagerReady = (
  ready: boolean,
  key: symbol = WEBMAP_MAP_MANAGER_KEY
) => {
  const state = instanceStates.get(key) || {
    isMapManagerReady: false,
    isMapViewerReady: false,
  }
  state.isMapManagerReady = ready
  instanceStates.set(key, state)
}

/**
 * Set MapViewer ready state for a given key
 * @param ready - Boolean flag indicating MapViewer is fully initialized
 * @param key - Optional symbol key for multi-map support (defaults to primary key)
 */
export const setMapViewerReady = (
  ready: boolean,
  key: symbol = WEBMAP_MAP_MANAGER_KEY
) => {
  const state = instanceStates.get(key)
  if (state) {
    state.isMapViewerReady = ready
  }
}

/**
 * Get MapManager ready state for a given key
 * @param key - Optional symbol key for multi-map support (defaults to primary key)
 * @returns true if MapManager instance exists and is ready
 */
export const getIsMapManagerReady = (
  key: symbol = WEBMAP_MAP_MANAGER_KEY
): boolean => {
  return instanceStates.get(key)?.isMapManagerReady ?? false
}

/**
 * Get MapViewer ready state for a given key
 * @param key - Optional symbol key for multi-map support (defaults to primary key)
 * @returns true if MapViewer is fully initialized ('map-x-ready' event fired)
 */
export const getIsMapViewerReady = (
  key: symbol = WEBMAP_MAP_MANAGER_KEY
): boolean => {
  return instanceStates.get(key)?.isMapViewerReady ?? false
}

/**
 * Debug logging for Map Manager Provider State changes
 */
// Store watch handler for cleanup
let mapXViewerDebugModeWatcher: (() => void) | null = null

/**
 * Turn on logging and Map logging group if it is turned off when
 * map_x_viewer_debug is enabled
 * This doesn't turn off the global logger or map group when disabled
 */
function ensureLoggingIsEnabled() {
  if (!isLoggingEnabled.value) {
    enableLogging()
  }

  if (!isGroupEnabled('Map')) {
    enableGroup('Map')
  }
}

// Watch the debug flag and start/stop logging accordingly
watch(
  isMapXViewerDebugActive,
  (isDebugActive) => {
    if (isDebugActive) {
      // Ensure logging is enabled when debug is turned on
      ensureLoggingIsEnabled()

      // Start watching if debug is enabled
      if (!mapXViewerDebugModeWatcher) {
        info('Map', 'Starting Map Manager Provider State debug logging...')
        mapXViewerDebugModeWatcher = watch(
          () => mapManagerProviderState.activeViewer,
          (newActiveViewer, oldActiveViewer) => {
            info('Map', 'Map Manager Provider State Changed:', {
              activeViewer: `${oldActiveViewer} → ${newActiveViewer}`,
            })
          }
        )
      }
    } else if (mapXViewerDebugModeWatcher) {
      // Stop watching if debug is disabled
      info('Map', 'Stopping Map Manager Provider State debug logging...')
      mapXViewerDebugModeWatcher()
      mapXViewerDebugModeWatcher = null
    }
  },
  { immediate: true }
)
